import React, { useCallback, useState } from 'react';
import {AddIcon, Button, Checkbox, Dialog, Flex, Input, Menu, MoreIcon, Popup, ProviderConsumer, SearchIcon} from '@fluentui/react-northstar';
import { FontIcon, MarqueeSelection } from '@fluentui/react';
import { DetailsList, DetailsListLayoutMode, IColumn, IDragDropContext, IDragDropEvents, Selection, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { markSortableColumns } from './columnDrag';
import { ResultCount } from '../ResultCount';
import { t } from 'i18next';
import { EmptyData } from '../EmptyData';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import EmptySvg from '../../../../svg/empty.svg';
import { DeleteIcon } from '@fluentui/react-icons-mdl2';
import UnarchiveSvg from '../../../../svg/Un-archive.svg';
import ArchiveSvg from '../../../../svg/archive-icon.svg';
import Dots from '../../../../svg/dots.svg';
import { toTitleCase } from '../../utils/helper';
import { ARCHIVE_CONFIRMATION_SUBTITLE, ARCHIVE_MODAL_HEADER, ERROR, LOADING, SETTINGS, SOMETHING_WENT_WRONG, TREE_LISTING_CONSTANTS, UNARCHIVE_MODAL_HEADER } from '../../utils/constants';
import { toolbarButtonColor } from '../../../tikit/ticketHelper';
import { Toolbar } from '../Toolbar';
import { Breadcrumbs } from '../BreadcrumbNavigation';
import { AxiosResponse } from 'axios';

export interface DraggableListTextContentProps {
    pageTitle: string;
    deleteItem: string;
    updateItem: string;
    emptyHeader: string;
    emptySubHeader: string;
    archiveStorageKey: string;
}

export interface DraggableListProps {
  columns: IColumn[];
  itemList: any[];
  setItemList: React.Dispatch<React.SetStateAction<any[]>>;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  showArchived: boolean;
  setShowArchived: React.Dispatch<React.SetStateAction<boolean>>;
  reorderItemsCallback: (items: any[]) => void;
  filteredList: any[];
  queryId: number;
  isLicensedUser: boolean;
  setQueryId: React.Dispatch<React.SetStateAction<number>>;
  setOpenEdit: React.Dispatch<React.SetStateAction<boolean>>;
  navs: Breadcrumbs;
  setAPIStatus: React.Dispatch<React.SetStateAction<APIStatus>>;
  fetchDataCallback: () => Promise<void>;
  deleteItemAPI: (id: number) => Promise<AxiosResponse<any>>;
  archiveItemAPI: (item: any) => Promise<AxiosResponse<any>>;
  textContents: DraggableListTextContentProps
}   

export const DraggableList = ({
    columns, 
    itemList, setItemList, 
    showArchived, setShowArchived, 
    search, setSearch,
    reorderItemsCallback, 
    filteredList,
    isLicensedUser,
    queryId, setQueryId,
    setOpenEdit,
    navs,
    setAPIStatus,
    fetchDataCallback,
    deleteItemAPI,
    archiveItemAPI,
    textContents
}: DraggableListProps) => {
    const [selection, _] = useState<Selection | undefined>();
    const [draggedItem, setDraggedItem] = useState<any>();
    const [openDelete, setOpenDelete] = useState(false);
    const [openArchive, setOpenArchive] = useState(false);
    const [archiveItem, setArchiveItem] = useState<any>({});

    const renderRecord = (item: any, key: string,) => {
        switch (key) {
            case 'options':
              return <DraggableListMenutItems dataItem={item} isLicensedUser={isLicensedUser} setQueryId={setQueryId} setOpenEdit={setOpenEdit} setOpenDelete={setOpenDelete} setOpenArchive={setOpenArchive} setArchiveItem={setArchiveItem}/>;
            case 'drag':
              return <Dots style={{ cursor: 'move' }} />;
        }
        return <></>;
    };

    const dragColumn: IColumn = { key: 'drag', name: ' ', minWidth: 20, maxWidth: 20, isMultiline: false, onRender: (item) => renderRecord(item, 'drag'), isResizable: false, isCollapsible: false };
    const optionsColumns: IColumn ={ key: 'options', name: ' ', minWidth: 35, maxWidth: 35, isMultiline: false, onRender: (item) => renderRecord(item,'options'), isResizable: false, isCollapsible: false };

    if(!columns.some(c => c.key === "drag"))
        columns.unshift(dragColumn);
    
    if(!columns.some(c => c.key === "options"))
        columns.push(optionsColumns);

    const dragDropEvents: IDragDropEvents = {
        canDrop: (dropContext?: IDragDropContext, dragContext?: IDragDropContext) => true,
        canDrag: (item?: any) => true,
        onDrop: (item?: any, event?: DragEvent) => {
            if (draggedItem) {
            const draggedItems = [draggedItem!];
            const insertIndex = itemList.indexOf(item);
            const items = itemList.filter(itm => draggedItems.indexOf(itm) === -1);
            items.splice(insertIndex, 0, ...draggedItems);

            setItemList(items);
            reorderItemsCallback(items);
            }
        },
        onDragStart: (item?: any, itemIndex?: number, selectedItems?: any[], event?: MouseEvent) => setDraggedItem(item),
        onDragEnd: (item?: any, event?: DragEvent) => setDraggedItem(undefined)
    }

     const deleteItemCallback = useCallback(async () => {
        setOpenDelete(false);
        setAPIStatus(prev => ({ ...prev, status: LOADING, msg: textContents.deleteItem }));

        try {
        await deleteItemAPI(queryId);
        fetchDataCallback();
        } catch (err: any) {
          console.log(`Exception while doing something: ${err}`);
          setAPIStatus(prev => ({ ...prev, status: ERROR, msg: SOMETHING_WENT_WRONG }));
        }
    }, [queryId]);

    const archiveItemCallback = useCallback(async () => {
        setOpenArchive(false);
        setAPIStatus(prev => ({ ...prev, status: LOADING, msg: textContents.updateItem }));
    
        try {
          const offer = itemList.find(x => x.Id == queryId);
          offer.IsArchived = !offer.IsArchived;
          await archiveItemAPI(offer);
          fetchDataCallback();
        } catch (err: any) {
          console.log(`Exception while doing something: ${err}`);
          setAPIStatus(prev => ({ ...prev, status: ERROR, msg: SOMETHING_WENT_WRONG }));
        }
      }, [queryId]);

    return (<ProviderConsumer render={(globalTheme) => 
        (<>

        <Toolbar globalTheme={globalTheme} title={`${toTitleCase(SETTINGS)} > ${textContents.pageTitle}`} breadcrumbs={navs} addNew={true}
            addNewContent={<Button icon={<AddIcon />} text primary content={`${t('common.new')} ${textContents.pageTitle}`} disabled={!isLicensedUser}
                onClick={() => { if (isLicensedUser) { setQueryId(0); setOpenEdit(true); } }}
                style={{ color: toolbarButtonColor(isLicensedUser, globalTheme.siteVariables.colorScheme), height: '52px' }} />} />

        {itemList.length > 0 && (<>
        <Flex style={{ justifyContent: 'flex-end', padding: 10 }} gap="gap.medium">
            <Checkbox label={t('preferences.show-archived')} toggle checked={showArchived} onChange={() => { localStorage.setItem(textContents.archiveStorageKey, JSON.stringify(!showArchived)); setShowArchived(!showArchived); }} />
            <Input icon={<SearchIcon />} inverted placeholder={t('common.search')} value={search} onChange={(e, p) => setSearch(p.value)} />
        </Flex>   
        </>)}

        <div className='flex' style={{ height: "calc(100vh - 160px - 2.25rem)" }}>
        <div className="w-full overflow-auto" data-is-scrollable="true">
            {itemList.length == 0 && <EmptyData headerText={textContents.emptyHeader} subheaderText={textContents.emptySubHeader} SVGIcon={<EmptySvg width={200} height={200} />} />}
            {itemList.length > 0 && <div className={`py-3.5 px-2.5 min-h-32 offering-list`} style={{ backgroundColor: 'var(--mgt-theme-background2)', ...ThemeColorScheme(globalTheme.siteVariables) }}>
            <MarqueeSelection selection={selection}>
                <DetailsList items={filteredList}
                // Rows here are ordered by dragging them, not by a column, so no header is clickable.
                columns={markSortableColumns(columns, () => false)}
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                selectionMode={SelectionMode.none}
                selection={selection}
                selectionPreservedOnEmptyClick={true}
                disableSelectionZone={true}
                enterModalSelectionOnTouch={true}
                dragDropEvents={dragDropEvents}
                ariaLabelForSelectionColumn='Toggle selection'
                ariaLabelForSelectAllCheckbox='Toggle selection for all items'
                checkButtonAriaLabel='select row' />
            </MarqueeSelection>
            </div>}
        </div>
        </div>
        {itemList.length > 0 && (<div className="h-9 flex items-center"><ResultCount count={filteredList.length} /></div>)}
        <Dialog cancelButton={t('common.buttons.cancel')} confirmButton={t('common.buttons.confirm')} open={openDelete} onConfirm={deleteItemCallback} onCancel={() => { setOpenDelete(false); }} header={t('service-offering.confirm-delete', { name: itemList.find(x => x.Id == queryId)?.Name ?? "" })} content={''} style={{ maxWidth: '500px' }} />
        <Dialog cancelButton={t('common.buttons.cancel')} confirmButton={t(archiveItem?.IsArchived ? TREE_LISTING_CONSTANTS.unArchive : TREE_LISTING_CONSTANTS.archive)} open={openArchive} onConfirm={archiveItemCallback} onCancel={() => { setOpenArchive(false); }}
            header={`${archiveItem?.IsArchived ? UNARCHIVE_MODAL_HEADER : ARCHIVE_MODAL_HEADER} ‘${archiveItem?.Name}’?`}
            content={`${ARCHIVE_CONFIRMATION_SUBTITLE} ‘${archiveItem?.Name}’ ${(textContents.pageTitle).toLowerCase()}?`} style={{ maxWidth: '500px' }} />
        </>)
    } />)
}

export interface DraggableListMenutItemProps {
    dataItem: any;
    isLicensedUser: boolean;
    setQueryId: React.Dispatch<React.SetStateAction<number>>;
    setOpenEdit: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenArchive: React.Dispatch<React.SetStateAction<boolean>>;
    setArchiveItem: React.Dispatch<React.SetStateAction<any>>;
}

export const DraggableListMenutItems = ({dataItem, isLicensedUser, setQueryId, setOpenEdit, setOpenDelete, setOpenArchive, setArchiveItem}: DraggableListMenutItemProps) => {
    const [openMenu, setOpenMenu] = useState<any>({});

    return <ProviderConsumer render={(globalTheme) => (<Popup position='below' className="p-0" open={openMenu[dataItem.Id] ?? false}
      onOpenChange={(e, { open }) => { if (isLicensedUser) setOpenMenu(prev => ({ ...prev, [dataItem.Id]: open })); }}
      trigger={<Button disabled={!isLicensedUser} icon={<MoreIcon />} circular text iconOnly title={t('knowledge.knowledge-table.more-options')} />}
      content={<Menu vertical pointing className='p-0' activeIndex={-1}
        items={[
          {
            content: t('common.edit'),
            icon: <FontIcon iconName={"Edit"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
            onClick: (e) => {
              e.stopPropagation();
              setOpenMenu(prev => ({ ...prev, [dataItem.Id]: false }));
              setQueryId(dataItem.Id);
              setOpenEdit(true);
            }
          },
          {
            content: dataItem.IsArchived ? t('common.tree-listing-constants.unArchive') : t('common.tree-listing-constants.archive'),
            icon: dataItem.IsArchived ? <UnarchiveSvg className={`text-lg`} style={{ lineHeight: "unset" }} fill={globalTheme.siteVariables.colorScheme.default.foreground} /> : <ArchiveSvg className={`text-lg`} style={{ lineHeight: "unset" }} fill={globalTheme.siteVariables.colorScheme.default.foreground} />,
            onClick: (e) => {
              e.stopPropagation();
              setOpenMenu(prev => ({ ...prev, [dataItem.Id]: false }));
              setQueryId(dataItem.Id);
              setArchiveItem(dataItem);
              setOpenArchive(true);
            }
          },
          {
            content: t('common.buttons.delete'),
            icon: <DeleteIcon className={`text-lg`} style={{ lineHeight: "unset" }} />,
            onClick: (e) => {
              e.stopPropagation();
              setOpenMenu(prev => ({ ...prev, [dataItem.Id]: false }));
              setQueryId(dataItem.Id);
              setOpenDelete(true);
            }
          }]} />} 
        />)
    } />
}